import React, { useEffect, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import DashboardItem from './DashboardItems/DashboardItem';
import mapImage from './mapImg.png';
import { getRoutes, createRoute } from '../../utils/api/routes';
import { handleResponse } from '../../utils/api/errorHandler';
import { useToasts } from '../../hooks';
import { withContextProvider } from '../../hoc';
import { DialogsProvider, ToastProvider } from '../../providers';
import { getNotes } from '../../utils/api/notes';
import { getVisits } from '../../utils/api/visits';
import { getSites } from '../../utils/api/site';
import Route from './DashboardItems/Route';
import Note from './DashboardItems/Note';
import Visit from './DashboardItems/Visit';
import Site from './DashboardItems/Site';
import AdvancedSearchWrapper from '../../componentsV2/AdvancedSearch/AdvancedSearchWrapper';
import { SITE_DEFAULT_SEARCH_PARAMS } from '../../constants/sites';
import cleanUpObject from '../../utils/cleanUpObject';
import HeaderIconTooltip from '../../componentsV2/HeaderIconTooltip/HeaderIconTooltip';
import RouteAddEditModal from '../../componentsV2/routes/Modal/RouteAddEditModal';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import { redirectToNewSite, redirectToRouteEdit } from '../../utils/navigation';
import NoteViewAddEditModal from '../../componentsV2/Notes/Modals/NoteViewAddEditModal';
import { useUrlModals } from '../../utils/useUrlModals';

const DashboardPage = ({ organizationId, currentUserId, currentUserRole }) => {
  const { showToast } = useToasts();
  const routesDropdownOptions = ['Most recent', 'Upcoming', 'Date'];
  const notesDropdownOptions = ['Priority', 'Status', 'Date'];
  const [itemsData, setItemsData] = useState({
    routes: [], notes: [], visits: [], sites: [],
  });
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [sitesAttributes, setSitesAttributes] = useState({});
  const [searchParams, setSearchParams] = useState({ ...SITE_DEFAULT_SEARCH_PARAMS });
  const [isRouteSaving, setIsRouteSaving] = useState(false);

  const { modals, openModal, closeModal } = useUrlModals({
    addRoute: { isIdParam: false },
    noteId: { isIdParam: true },
    editMode: { isIdParam: false },
  });

  // Access modals
  const routeModal = modals.addRoute || { isOpen: false, value: null };
  const noteModal = modals.noteId || { isOpen: false, value: null };
  const editModeModal = modals.editMode || { isOpen: false, value: null }; // <-- ADD THIS LINE

  // Functions to open/close edit mode
  const handleOpenEditMode = () => openModal('editMode', 'true');
  const handleCloseEditMode = () => closeModal('editMode');
  const handleCloseNoteModal = () => {
    closeModal('noteId');
    closeModal('editMode');
  };

  useEffect(() => {
    fetch(`/api/v3/sites_attributes?organization_id=${organizationId}`)
      .then(handleResponse)
      .then(data => {
        setSitesAttributes(data);
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  }, [organizationId, showToast]);

  const [routeSortOption, setRouteSortOption] = useState('Date');
  const [noteSortOption, setNoteSortOption] = useState('Date');

  const fetchSites = useCallback((orgId) => {
    const params = {
      ...SITE_DEFAULT_SEARCH_PARAMS,
      organization_id: orgId,
      sort: 'updated_at',
      ordering: 'desc',
    };

    getSites(params)
      .then((data) => {
        if (!data.sites) {
          throw new Error(data.error || 'Failed to fetch sites');
        }
        setItemsData(prev => ({ ...prev, sites: data.sites }));
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  }, [showToast]);

  const fetchSortedRoutes = useCallback((orgId, sortOption) => {
    let params = {};
    if (sortOption === 'Most recent') {
      params = {
        routesEndDate: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
        sort_by: 'date',
        direction: 'desc',
      };
    } else if (sortOption === 'Upcoming') {
      params = {
        routesStartDate: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
        sort_by: 'date',
        direction: 'asc',
      };
    } else if (sortOption === 'Date') {
      params = {
        sort_by: 'date',
      };
    }
    getRoutes(orgId, params)
      .then(handleResponse)
      .then(data => {
        setItemsData(prev => ({ ...prev, routes: data.routes }));
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  }, [showToast]);

  const fetchSortedNotes = useCallback((orgId, sortOption) => {
    let params = {};
    if (sortOption === 'Priority') {
      params = {
        sort: 'priority',
        ordering: 'desc',
      };
    } else if (sortOption === 'Status') {
      params = {
        sort: 'status',
        ordering: 'desc',
      };
    } else if (sortOption === 'Date') {
      params = {
        ordering: 'desc',
      };
    }
    getNotes(orgId, params)
      .then(handleResponse)
      .then(data => {
        setItemsData(prev => ({ ...prev, notes: data.notes }));
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  }, [showToast]);

  const fetchVisits = useCallback((orgId) => {
    getVisits(orgId)
      .then(handleResponse)
      .then(data => {
        setItemsData(prev => ({ ...prev, visits: data.visits }));
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  }, [showToast]);

  useEffect(() => {
    fetchSortedRoutes(organizationId, routeSortOption);
  }, [organizationId, routeSortOption, fetchSortedRoutes]);

  useEffect(() => {
    fetchSortedNotes(organizationId, noteSortOption);
  }, [organizationId, noteSortOption, fetchSortedNotes]);

  const handleNotesChange = useCallback(() => {
    fetchSortedNotes(organizationId, noteSortOption);
  }, [fetchSortedNotes, organizationId, noteSortOption]);

  useEffect(() => {
    fetchVisits(organizationId);
  }, [organizationId, fetchVisits]);

  useEffect(() => {
    fetchSites(organizationId);
  }, [organizationId, fetchSites]);

  const handleRouteSortChange = (option) => {
    setRouteSortOption(option);
  };

  const handleNoteSortChange = (option) => {
    setNoteSortOption(option);
  };

  const updateSearchParamsAndUrl = (params) => {
    setSearchParams(params);
    cleanUpObject(params);
    const newURL = new URLSearchParams(params).toString();
    window.location.replace(`/sites?${newURL}`);
  };
  const search = () => {
    // Update searchParams with the reset offset
    const updatedSearchParams = { ...searchParams, organization_id: organizationId };
    updateSearchParamsAndUrl(updatedSearchParams);
  };

  const handleRouteSubmit = (data) => {
    setIsRouteSaving(true);
    createRoute(data, organizationId)
      .then(handleResponse)
      .then((res) => {
        fetchSortedRoutes(organizationId, routeSortOption);
        showToast({ message: 'Route created!', type: 'success' });
        closeModal('addRoute');

        setTimeout(() => {
          redirectToRouteEdit(res?.route.id);
        }, 0);
      })

      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      })
      .finally(() => {
        setIsRouteSaving(false);
      });
  };

  return (
    <DialogsProvider>
      <div className="mx-2.75">
        <header>
          <div className="flex justify-between items-center pb-6">
            {/* Title */}
            <PagesHeader name="dashboard" label="Dashboard" size="h-9" />
            {/* Search Section */}
            <div className="flex items-center">
              <AdvancedSearchWrapper
                setIsSearchExpanded={setIsSearchExpanded}
                sitesAttributes={sitesAttributes}
                params={searchParams}
                setParams={setSearchParams}
                search={search}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                updateSearchParamsAndUrl={updateSearchParamsAndUrl}
                isSearchExpanded={isSearchExpanded}
              />
              <div className="w-px h-11 bg-vamos-blue-gray mx-4" />
              <button
                type="button"
                onClick={() => {
                  window.location.href = '/sites?mode=map';
                }}
              >
                <HeaderIconTooltip
                  divTextStart="See all of your organization’s sites"
                  divTextEnd="on a map."
                  place="bottom-end"
                  icon={<img src={mapImage} alt="map" className="cursor-pointer" />}
                  paddingBottom="0"
                  className="p-0 text-left"
                  margins="mr-3.75 mb-0 my-auto"
                  border="border-0"
                />
              </button>

            </div>
          </div>
        </header>
        <div className="grid grid-cols-1 gap-4 w-full md:grid-cols-2 md:grid-rows-2">
          <DashboardItem
            itemsList={itemsData.sites.map(site => <Site site={site} key={`site-${site.id}`} />) || ''}
            title="Sites"
            type="sites"
            shouldShowAddBtn
            onClickAddButton={redirectToNewSite}
            shouldShowDropdown={false}
          />
          <DashboardItem
            itemsList={itemsData.routes.map((route) => (<Route route={route} key={`route-${route.id}`} onClickRoute={() => openModal('addRoute', route.id)} />)) || ''}
            title="Routes"
            type="routes"
            organizationId={organizationId}
            dropdownOptions={routesDropdownOptions}
            onSortChange={handleRouteSortChange}
            onClickAddButton={() => openModal('addRoute')}
            shouldShowDropdown={false}
          />
          <DashboardItem
            itemsList={itemsData.visits.map(visit => <Visit visit={visit} key={`visit-${visit.id}`} />)}
            title="Visits"
            type="visits"
            organizationId={organizationId}
            shouldShowAddBtn={false}
            shouldShowDropdown={false}
          />
          <DashboardItem
            itemsList={itemsData.notes.map((note) => (<Note note={note} key={`note-${note.id}`} onClickNote={() => openModal('noteId', note.id)} />))}
            title="Notes"
            type="notes"
            organizationId={organizationId}
            shouldShowAddBtn={false}
            shouldShowDropdown={false}
            dropdownOptions={notesDropdownOptions}
            onSortChange={handleNoteSortChange}
          />
        </div>
        {routeModal.isOpen && (
          <RouteAddEditModal
            onCloseModal={() => closeModal('addRoute')}
            title="Create Route"
            onSubmit={handleRouteSubmit}
            isSaving={isRouteSaving}
          />
        )}
        {noteModal.isOpen && (
          <NoteViewAddEditModal
            activeNoteId={noteModal.value}
            organizationId={organizationId}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            setOpenAddEditModal={handleCloseNoteModal}
            showToast={showToast}
            isEditMode={editModeModal.isOpen}
            onOpenEditMode={handleOpenEditMode}
            onCloseEditMode={handleCloseEditMode}
            onNotesChange={handleNotesChange}
          />
        )}
      </div>
    </DialogsProvider>
  );
};

DashboardPage.propTypes = {
  organizationId: PropTypes.number.isRequired,
  currentUserId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default withContextProvider(ToastProvider)(DashboardPage);
